import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ActiveAuctionsTab - View and manage live/scheduled auctions
 * Displays table of active and scheduled auctions with controls
 */
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaSync, FaGavel, FaStopCircle, FaClock, FaUsers, FaFilter, FaEye, } from 'react-icons/fa';
import { auctionService } from '../../../services/auctionService';
import { useAuctionAdmin } from '../../../hooks/useAuctionAdmin';
// ── Styled Components ────────────────────────────────────────
const Container = styled.div `
  padding: 0;
`;
const HeaderActions = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;
const ActionGroup = styled.div `
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;
const Button = styled.button `
  padding: 0.65rem 1.25rem;
  border-radius: 6px;
  border: 1px solid ${props => props.$danger ? '#ef4444'
    : props.$primary ? 'var(--accent-gold)'
        : 'var(--border-color)'};
  background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.1)'
    : props.$primary ? 'var(--accent-gold)'
        : 'var(--bg-secondary)'};
  color: ${props => props.$danger ? '#ef4444'
    : props.$primary ? 'white'
        : 'var(--text-primary)'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
const FilterSelect = styled.select `
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.85rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
  }
`;
const CountBadge = styled.span `
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--accent-gold);
  color: white;
`;
const TableContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow-x: auto;
  max-width: 100%;
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
`;
const Th = styled.th `
  text-align: left;
  padding: 1rem;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-primary);
  white-space: nowrap;
`;
const Td = styled.td `
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.875rem;
  color: var(--text-primary);
  vertical-align: middle;
`;
const Tr = styled.tr `
  transition: background 0.15s;

  &:hover {
    background: var(--bg-tertiary);
  }

  &:last-child td {
    border-bottom: none;
  }
`;
const StatusBadge = styled.span `
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch (props.$status) {
        case 'active': return 'rgba(16, 185, 129, 0.15)';
        case 'scheduled': return 'rgba(59, 130, 246, 0.15)';
        case 'grace_period': return 'rgba(245, 158, 11, 0.15)';
        case 'closing': return 'rgba(249, 115, 22, 0.15)';
        case 'draft': return 'rgba(107, 114, 128, 0.15)';
        default: return 'rgba(107, 114, 128, 0.15)';
    }
}};
  color: ${props => {
    switch (props.$status) {
        case 'active': return '#10b981';
        case 'scheduled': return '#3b82f6';
        case 'grace_period': return '#f59e0b';
        case 'closing': return '#f97316';
        case 'draft': return '#6b7280';
        default: return '#6b7280';
    }
}};
`;
const TypeBadge = styled.span `
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$type === 'supervised' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(6, 182, 212, 0.15)'};
  color: ${props => props.$type === 'supervised' ? '#8b5cf6' : '#06b6d4'};
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: var(--accent-gold);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
`;
const BidCount = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--text-secondary);

  svg {
    font-size: 0.7rem;
    color: var(--text-tertiary);
  }
`;
const TimeRemaining = styled.span `
  font-size: 0.85rem;
  font-weight: 500;
  color: ${props => props.$urgent ? '#ef4444' : 'var(--text-secondary)'};
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  svg {
    font-size: 0.7rem;
  }
`;
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;
const ActionButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => {
    switch (props.$variant) {
        case 'primary':
            return `
          background: rgba(139, 92, 246, 0.1);
          border-color: #8b5cf6;
          color: #8b5cf6;
          &:hover { background: rgba(139, 92, 246, 0.2); }
        `;
        case 'danger':
            return `
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
          &:hover { background: rgba(239, 68, 68, 0.2); }
        `;
        case 'link':
            return `
          background: rgba(59, 130, 246, 0.1);
          border-color: #3b82f6;
          color: #3b82f6;
          &:hover { background: rgba(59, 130, 246, 0.2); }
        `;
        default:
            return `
          background: var(--bg-tertiary);
          border-color: var(--border-color);
          color: var(--text-secondary);
          &:hover { background: var(--bg-secondary); }
        `;
    }
}}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-tertiary);

  svg {
    font-size: 4rem;
    color: var(--accent-gold);
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
const LoadingState = styled.div `
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;
const ConfirmOverlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ConfirmDialog = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;

  h3 {
    margin: 0 0 0.75rem 0;
    color: var(--text-primary);
  }

  p {
    margin: 0 0 1.5rem 0;
    color: var(--text-secondary);
    font-size: 0.9rem;
  }
`;
const ConfirmActions = styled.div `
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`;
// ── Component ────────────────────────────────────────────────
const ActiveAuctionsTab = () => {
    const [loading, setLoading] = useState(true);
    const [auctions, setAuctions] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [cancellingId, setCancellingId] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(null);
    const navigate = useNavigate();
    const { cancelAuction } = useAuctionAdmin();
    const fetchAuctions = useCallback(async () => {
        setLoading(true);
        try {
            const params = { pageSize: 50 };
            if (statusFilter) {
                params.status = statusFilter;
            }
            if (typeFilter) {
                params.type = typeFilter;
            }
            const result = await auctionService.listAuctions(params);
            if (result.success && result.data) {
                let filtered = result.data.auctions;
                if (!statusFilter) {
                    filtered = filtered.filter(a => ['draft', 'scheduled', 'active', 'grace_period', 'closing'].includes(a.status));
                }
                setAuctions(filtered);
            }
        }
        catch {
            // Silently fail - will show empty state
        }
        finally {
            setLoading(false);
        }
    }, [statusFilter, typeFilter]);
    useEffect(() => {
        fetchAuctions();
    }, [fetchAuctions]);
    const handleCancelAuction = async (auction) => {
        setCancellingId(auction.auctionId);
        try {
            const result = await cancelAuction(auction.auctionId);
            if (result.success) {
                await fetchAuctions();
            }
        }
        finally {
            setCancellingId(null);
            setConfirmCancel(null);
        }
    };
    const formatTimeRemaining = (endTime) => {
        const end = new Date(endTime);
        const now = new Date();
        const diff = end.getTime() - now.getTime();
        if (diff <= 0)
            return { text: 'Ended', urgent: true };
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 24)
            return { text: `${Math.floor(hours / 24)}d ${hours % 24}h`, urgent: false };
        if (hours > 0)
            return { text: `${hours}h ${minutes}m`, urgent: hours < 2 };
        return { text: `${minutes}m`, urgent: true };
    };
    const formatStartTime = (startTime) => {
        const start = new Date(startTime);
        const now = new Date();
        const diff = start.getTime() - now.getTime();
        if (diff <= 0)
            return 'Starting...';
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 24)
            return `in ${Math.floor(hours / 24)}d`;
        if (hours > 0)
            return `in ${hours}h`;
        const minutes = Math.floor(diff / (1000 * 60));
        return `in ${minutes}m`;
    };
    if (loading) {
        return (_jsx(Container, { children: _jsx(LoadingState, { children: "Loading auctions..." }) }));
    }
    return (_jsxs(Container, { children: [_jsxs(HeaderActions, { children: [_jsxs(ActionGroup, { children: [_jsxs(Button, { "$primary": true, onClick: fetchAuctions, disabled: loading, children: [_jsx(FaSync, {}), " Refresh"] }), _jsxs(CountBadge, { children: [auctions.length, " auction", auctions.length !== 1 ? 's' : ''] })] }), _jsxs(ActionGroup, { children: [_jsx(FaFilter, { style: { color: 'var(--text-tertiary)', fontSize: '0.8rem' } }), _jsxs(FilterSelect, { value: statusFilter, onChange: e => setStatusFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Active" }), _jsx("option", { value: "active", children: "Live" }), _jsx("option", { value: "scheduled", children: "Scheduled" }), _jsx("option", { value: "grace_period", children: "Grace Period" }), _jsx("option", { value: "draft", children: "Draft" })] }), _jsxs(FilterSelect, { value: typeFilter, onChange: e => setTypeFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Types" }), _jsx("option", { value: "automated", children: "Automated" }), _jsx("option", { value: "supervised", children: "Supervised" })] })] })] }), auctions.length > 0 ? (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Type" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Current Bid" }), _jsx(Th, { children: "Bids" }), _jsx(Th, { children: "Time" }), _jsx(Th, { style: { textAlign: 'right' }, children: "Actions" })] }) }), _jsx("tbody", { children: auctions.map(auction => {
                                const isLive = auction.status === 'active' || auction.status === 'grace_period';
                                const isScheduled = auction.status === 'scheduled';
                                const timeInfo = isLive
                                    ? formatTimeRemaining(auction.scheduledEndTime)
                                    : null;
                                return (_jsxs(Tr, { children: [_jsxs(Td, { children: [_jsxs("strong", { children: [auction.diamondSnapshot.caratWeight, "ct", ' ', auction.diamondSnapshot.shape, ' ', auction.diamondSnapshot.color, ' ', auction.diamondSnapshot.clarity] }), _jsx("div", { style: { fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }, children: auction.diamondStockNumber || auction.diamondSnapshot.certificateNumber || '-' })] }), _jsx(Td, { children: _jsx(TypeBadge, { "$type": auction.auctionType, children: auction.auctionType }) }), _jsx(Td, { children: _jsx(StatusBadge, { "$status": auction.status, children: auction.status === 'grace_period' ? 'Grace' : auction.status }) }), _jsxs(Td, { children: [_jsx(PriceCell, { children: auction.currentBid > 0
                                                        ? `$${auction.currentBid.toLocaleString()}`
                                                        : `$${auction.startingPrice.toLocaleString()}` }), auction.currentBid === 0 && (_jsx("div", { style: { fontSize: '0.7rem', color: 'var(--text-tertiary)' }, children: "starting" }))] }), _jsx(Td, { children: _jsxs(BidCount, { children: [_jsx(FaUsers, {}), " ", auction.bidCount] }) }), _jsx(Td, { children: isLive && timeInfo ? (_jsxs(TimeRemaining, { "$urgent": timeInfo.urgent, children: [_jsx(FaClock, {}), " ", timeInfo.text] })) : isScheduled ? (_jsxs(TimeRemaining, { children: [_jsx(FaClock, {}), " ", formatStartTime(auction.scheduledStartTime)] })) : (_jsx("span", { style: { color: 'var(--text-tertiary)', fontSize: '0.85rem' }, children: "-" })) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [auction.auctionType === 'supervised' && isLive && (_jsxs(ActionButton, { "$variant": "primary", title: "Open live control panel", onClick: () => navigate(`/auctions/${auction.auctionId}`), children: [_jsx(FaGavel, {}), " Control"] })), _jsxs(ActionButton, { "$variant": "link", title: "View auction details", onClick: () => navigate(`/auctions/${auction.auctionId}`), children: [_jsx(FaEye, {}), " View"] }), ['scheduled', 'active', 'grace_period', 'draft'].includes(auction.status) && (_jsxs(ActionButton, { "$variant": "danger", onClick: () => setConfirmCancel(auction), disabled: cancellingId === auction.auctionId, title: "Cancel auction", children: [_jsx(FaStopCircle, {}), cancellingId === auction.auctionId ? '...' : 'Cancel'] }))] }) })] }, auction.id));
                            }) })] }) })) : (_jsxs(EmptyState, { children: [_jsx(FaGavel, {}), _jsx("h3", { children: "No Active Auctions" }), _jsx("p", { children: statusFilter
                            ? `No auctions match the "${statusFilter}" filter.`
                            : 'Create a new auction from the "Create Auction" tab.' })] })), confirmCancel && (_jsx(ConfirmOverlay, { onClick: () => setConfirmCancel(null), children: _jsxs(ConfirmDialog, { onClick: e => e.stopPropagation(), children: [_jsx("h3", { children: "Cancel Auction?" }), _jsxs("p", { children: ["Are you sure you want to cancel the auction for", ' ', _jsxs("strong", { children: [confirmCancel.diamondSnapshot.caratWeight, "ct", ' ', confirmCancel.diamondSnapshot.shape, ' ', confirmCancel.diamondSnapshot.color, ' ', confirmCancel.diamondSnapshot.clarity] }), "?", confirmCancel.bidCount > 0 && (_jsxs("span", { style: { display: 'block', color: '#f59e0b', marginTop: '0.5rem' }, children: ["This auction has ", confirmCancel.bidCount, " bid", confirmCancel.bidCount !== 1 ? 's' : '', ". All bidders will be notified."] }))] }), _jsxs(ConfirmActions, { children: [_jsx(Button, { onClick: () => setConfirmCancel(null), children: "Keep Auction" }), _jsxs(Button, { "$danger": true, onClick: () => handleCancelAuction(confirmCancel), disabled: cancellingId === confirmCancel.auctionId, children: [_jsx(FaStopCircle, {}), cancellingId === confirmCancel.auctionId ? 'Cancelling...' : 'Yes, Cancel'] })] })] }) }))] }));
};
export default ActiveAuctionsTab;
